import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, GripVertical, Save, Check, Settings } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { fieldConfigService } from '../services/fieldConfigService';
import { useSelector } from 'react-redux';

const FIELD_TYPES = [
  { value: 'text', label: 'Text (Single Line)' },
  { value: 'textarea', label: 'Textarea (Multi-line)' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown/Select' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File Upload' },
];

const FieldManager = ({ designation, entityType, isOpen, onClose, onSave, isTemporary }) => {
  const { success, error } = useToast();
  const { user } = useSelector((state) => state.auth);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showFieldForm, setShowFieldForm] = useState(false);
  
  const [fieldForm, setFieldForm] = useState({
    fieldName: '',
    fieldLabel: '',
    fieldType: 'text',
    required: false,
    placeholder: '',
    helpText: '',
    options: '',
    validation: {
      minLength: '',
      maxLength: '',
      min: '',
      max: '',
      pattern: '',
    },
  });

  // Auto-generate field name from field label
  const generateFieldName = (label) => {
    return label
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .split(/\s+/) // Split by spaces
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase(); // First word lowercase
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Others capitalized
      })
      .join('');
  };

  // Get default fields based on designation type
  const getDefaultFields = (fieldType) => {
    // Common fields for all staff
    const commonFields = [
      { _id: 'default_name', fieldName: 'name', fieldLabel: 'Full Name', fieldType: 'text', required: true, placeholder: 'Enter full name', helpText: 'Staff member\'s complete name', options: [], validation: { minLength: 2, maxLength: 100 } },
      { _id: 'default_email', fieldName: 'email', fieldLabel: 'Email Address', fieldType: 'email', required: true, placeholder: 'name@example.com', helpText: 'Official email address', options: [], validation: {} },
      { _id: 'default_phone', fieldName: 'phone', fieldLabel: 'Phone Number', fieldType: 'phone', required: true, placeholder: '+91 1234567890', helpText: 'Primary contact number', options: [], validation: {} },
      { _id: 'default_dob', fieldName: 'dateOfBirth', fieldLabel: 'Date of Birth', fieldType: 'date', required: true, placeholder: '', helpText: 'Staff member\'s date of birth', options: [], validation: {} },
      { _id: 'default_gender', fieldName: 'gender', fieldLabel: 'Gender', fieldType: 'radio', required: true, placeholder: '', helpText: 'Select gender', options: ['Male', 'Female', 'Other'], validation: {} },
      { _id: 'default_marital', fieldName: 'maritalStatus', fieldLabel: 'Marital Status', fieldType: 'dropdown', required: true, placeholder: '', helpText: 'Current marital status', options: ['Single', 'Married', 'Divorced', 'Widowed'], validation: {} },
      { _id: 'default_employment_type', fieldName: 'employmentType', fieldLabel: 'Employment Type', fieldType: 'radio', required: true, placeholder: '', helpText: 'Full-time or part-time employment', options: ['Full-time', 'Part-time'], validation: {} },
      { _id: 'default_mother_tongue', fieldName: 'motherTongue', fieldLabel: 'Mother Tongue', fieldType: 'text', required: false, placeholder: 'e.g., Hindi, English, Tamil', helpText: 'First/native language', options: [], validation: {} },
      { _id: 'default_nationality', fieldName: 'nationality', fieldLabel: 'Nationality', fieldType: 'text', required: true, placeholder: 'e.g., Indian', helpText: 'Country of citizenship', options: [], validation: {} },
      { _id: 'default_aadhar', fieldName: 'aadharNumber', fieldLabel: 'Aadhar Number', fieldType: 'text', required: true, placeholder: '1234 5678 9012', helpText: '12-digit Aadhar identification number', options: [], validation: { minLength: 12, maxLength: 14 } },
      { _id: 'default_pan', fieldName: 'pan', fieldLabel: 'PAN Number', fieldType: 'text', required: false, placeholder: 'ABCDE1234F', helpText: 'Permanent Account Number (Optional)', options: [], validation: { minLength: 10, maxLength: 10 } },
      { _id: 'default_blood', fieldName: 'bloodGroup', fieldLabel: 'Blood Group', fieldType: 'dropdown', required: false, placeholder: '', helpText: 'Blood type', options: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'], validation: {} },
      { _id: 'default_address', fieldName: 'address', fieldLabel: 'Address', fieldType: 'textarea', required: true, placeholder: 'Enter complete address', helpText: 'Current residential address', options: [], validation: { minLength: 10, maxLength: 500 } },
      { _id: 'default_salary', fieldName: 'salary', fieldLabel: 'Salary', fieldType: 'number', required: true, placeholder: 'e.g., 50000', helpText: 'Monthly salary amount', options: [], validation: { min: 0 } },
      { _id: 'default_emergency', fieldName: 'emergencyContacts', fieldLabel: 'Emergency Contacts', fieldType: 'textarea', required: true, placeholder: 'Name: John Doe\nRelation: Spouse\nPhone: +91 9876543210', helpText: 'Emergency contact person details', options: [], validation: {} },
    ];

    if (fieldType === 'teaching') {
      return [
        ...commonFields,
        { _id: 'default_subjects', fieldName: 'subjects', fieldLabel: 'Subjects', fieldType: 'textarea', required: true, placeholder: 'e.g., Mathematics, Physics, Chemistry', helpText: 'Subjects taught by this teacher', options: [], validation: {} },
        { _id: 'default_academic_year', fieldName: 'academicYear', fieldLabel: 'Academic Year', fieldType: 'text', required: true, placeholder: 'e.g., 2024-2025', helpText: 'Current academic year', options: [], validation: {} },
        { _id: 'default_incharge', fieldName: 'inChargeOf', fieldLabel: 'In-Charge Of', fieldType: 'text', required: false, placeholder: 'Selected class will appear here', helpText: 'Class this teacher is in charge of', options: [], validation: {} },
        { _id: 'default_qualification', fieldName: 'qualification', fieldLabel: 'Qualification', fieldType: 'text', required: true, placeholder: 'e.g., B.Ed, M.Sc in Mathematics', helpText: 'Educational qualifications', options: [], validation: {} },
        { _id: 'default_experience', fieldName: 'experienceInYears', fieldLabel: 'Years of Experience', fieldType: 'number', required: true, placeholder: 'e.g., 5', helpText: 'Total years of teaching experience', options: [], validation: { min: 0, max: 50 } },
      ];
    } else if (fieldType === 'non-teaching') {
      return [
        ...commonFields,
        { _id: 'default_role', fieldName: 'role', fieldLabel: 'Role/Responsibility', fieldType: 'text', required: true, placeholder: 'e.g., Office Manager, Accountant, Librarian', helpText: 'Primary role and responsibilities', options: [], validation: {} },
        { _id: 'default_qualification', fieldName: 'qualification', fieldLabel: 'Qualification', fieldType: 'text', required: false, placeholder: 'e.g., B.Com, Diploma', helpText: 'Educational or professional qualifications', options: [], validation: {} },
      ];
    }
    return [];
  };

  useEffect(() => {
    if (isOpen && (designation || entityType)) {
      if (isTemporary) {
        // For temporary designations, start with default fields based on type
        const defaultFields = designation ? getDefaultFields(designation.fieldType) : [];
        setFields(defaultFields);
      } else {
        // For existing designations, load fields and auto-restore if empty
        loadFields(true);
      }
    }
  }, [isOpen, designation, entityType, isTemporary]);

  const loadFields = async (autoRestoreIfEmpty = false) => {
    setLoading(true);
    try {
      let data = [];
      if (entityType) {
        const response = await fieldConfigService.getEntityFields(entityType, user.token);
        data = Array.isArray(response) ? response : (response.data || []);
      } else if (designation) {
        const response = await fieldConfigService.getDesignationFields(designation._id, user.token);
        data = Array.isArray(response) ? response : (response.data || []);
      }
      
      if (data.length === 0 && designation && !entityType && autoRestoreIfEmpty) {
        console.log('FieldManager: No fields found, auto-restoring defaults');
        await performRestore(true); // Silent restore
      } else {
        setFields(data);
      }
    } catch (err) {
      console.error('FieldManager: Error loading fields:', err);
      error('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFieldForm({
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      required: false,
      placeholder: '',
      helpText: '',
      options: '',
      validation: {
        minLength: '',
        maxLength: '',
        min: '',
        max: '',
        pattern: '',
      },
    });
    setEditingField(null);
    setShowFieldForm(false);
  };

  const handleEdit = (field) => {
    console.log('FieldManager: Entering edit mode for field', field);
    setEditingField(field);
    setFieldForm({
      fieldName: field.fieldName || '',
      fieldLabel: field.fieldLabel || '',
      fieldType: field.fieldType || 'text',
      required: !!field.required,
      placeholder: field.placeholder || '',
      helpText: field.helpText || '',
      options: Array.isArray(field.options) ? field.options.join('\n') : '',
      validation: {
        minLength: field.validation?.minLength || '',
        maxLength: field.validation?.maxLength || '',
        min: field.validation?.min || '',
        max: field.validation?.max || '',
        pattern: field.validation?.pattern || '',
      },
    });
    setShowFieldForm(true);
    // Scroll to top of modal content to ensure form is visible
    const contentArea = document.getElementById('field-manager-content');
    if (contentArea) contentArea.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    console.log('FieldManager: Saving field', fieldForm);
    
    // Ensure fieldName is generated if it's missing (should be auto-generated from label but double check)
    const finalFieldName = fieldForm.fieldName || generateFieldName(fieldForm.fieldLabel);

    if (!finalFieldName || !fieldForm.fieldLabel) {
      error('Field name and label are required');
      return;
    }

    const fieldData = {
      fieldName: finalFieldName,
      fieldLabel: fieldForm.fieldLabel,
      fieldType: fieldForm.fieldType,
      required: fieldForm.required,
      placeholder: fieldForm.placeholder,
      helpText: fieldForm.helpText,
      options: fieldForm.options ? fieldForm.options.split('\n').filter(o => o.trim()) : [],
      validation: {
        minLength: fieldForm.validation.minLength ? parseInt(fieldForm.validation.minLength) : undefined,
        maxLength: fieldForm.validation.maxLength ? parseInt(fieldForm.validation.maxLength) : undefined,
        min: fieldForm.validation.min ? parseInt(fieldForm.validation.min) : undefined,
        max: fieldForm.validation.max ? parseInt(fieldForm.validation.max) : undefined,
        pattern: fieldForm.validation.pattern || undefined,
      },
    };

    if (isTemporary) {
      // For temporary designations, just store in local state
      if (editingField) {
        setFields(fields.map(f => f._id === editingField._id ? { ...fieldData, _id: editingField._id } : f));
        success('Field updated');
      } else {
        setFields([...fields, { ...fieldData, _id: 'temp_' + Date.now() }]);
        success('Field added');
      }
      resetForm();
    } else {
      // For existing designations or entities, save to DB
      try {
        const targetId = entityType ? 'global' : designation._id;
        
        if (editingField) {
          await fieldConfigService.updateField(targetId, editingField._id, fieldData, user.token);
          success('Field updated successfully');
        } else {
          const payload = entityType ? { ...fieldData, entityType } : fieldData;
          await fieldConfigService.addField(targetId, payload, user.token);
          success('Field added successfully');
        }

        await loadFields();
        resetForm();
      } catch (err) {
        error(err.response?.data?.message || 'Failed to save field');
      }
    }
  };

  const handleDelete = async (field) => {
    if (field.isSystemField) {
      error('Cannot delete system field');
      return;
    }

    if (!window.confirm(`Delete field "${field.fieldLabel}"?`)) {
      return;
    }

    if (isTemporary) {
      // For temporary designations, just remove from local state
      setFields(fields.filter(f => f._id !== field._id));
      success('Field removed');
    } else {
      try {
        const targetId = entityType ? 'global' : designation._id;
        await fieldConfigService.deleteField(targetId, field._id, user.token);
        success('Field deleted');
        await loadFields();
      } catch (err) {
        error(err.response?.data?.message || 'Failed to delete field');
      }
    }
  };

  const performRestore = async (silent = false) => {
    if (!designation) return;
    
    if (!silent && !window.confirm(`Restore standard fields for "${designation.name}"? This will add common mandatory fields like Qualification and Experience.`)) {
      return;
    }

    setLoading(true);
    try {
      const defaultFields = getDefaultFields(designation.fieldType);
      const targetId = entityType ? 'global' : designation._id;
      
      // Fetch current fields to avoid duplicates
      let currentFields = [];
      try {
        const resp = await fieldConfigService.getDesignationFields(designation._id, user.token);
        currentFields = Array.isArray(resp) ? resp : (resp.data || []);
      } catch (e) {
        console.warn('Could not fetch existing fields for deduplication');
      }

      for (const field of defaultFields) {
        // Skip if field already exists by name
        if (currentFields.some(f => f.fieldName === field.fieldName)) continue;
        
        const { _id, ...fieldData } = field;
        const payload = entityType ? { ...fieldData, entityType } : fieldData;
        await fieldConfigService.addField(targetId, payload, user.token);
      }
      
      if (!silent) success('Standard fields restored successfully');
      await loadFields(false); // Reload without auto-restore to avoid loop
    } catch (err) {
      console.error('FieldManager: Error restoring defaults:', err);
      if (!silent) error('Failed to restore standard fields');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDefaults = () => performRestore(false);

  const handleFinish = () => {
    if (isTemporary && onSave) {
      // Send fields back to parent component to save with designation
      onSave(fields);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col premium-shadow">
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between primary-gradient-bg">
          <div className="flex items-center space-x-4">
            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
              <Settings className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">
                {isTemporary ? 'Configure Fields' : 'Manage Fields'}
              </h2>
              <p className="text-blue-100 text-[10px] font-black uppercase tracking-[0.2em] mt-1">
                {designation?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div id="field-manager-content" className="flex-1 overflow-y-auto p-6 scroll-smooth">
          {/* Actions */}
          {!showFieldForm && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowFieldForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={20} />
                Add Custom Field
              </button>
              
              {!isTemporary && designation && (
                <button
                  onClick={handleRestoreDefaults}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2 border border-slate-200"
                >
                  <Plus size={18} />
                  Restore Standard Fields
                </button>
              )}
            </div>
          )}

          {/* Field Form */}
          {showFieldForm && (
            <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                {editingField ? 'Edit Field' : 'New Field'}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Field Label *</label>
                    <input
                      type="text"
                      value={fieldForm.fieldLabel}
                      onChange={(e) => {
                        const label = e.target.value;
                        setFieldForm({ 
                          ...fieldForm, 
                          fieldLabel: label,
                          // Only auto-generate fieldName for NEW fields
                          fieldName: editingField ? fieldForm.fieldName : generateFieldName(label)
                        });
                      }}
                      placeholder="e.g., Years of Experience, Department Name"
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium"
                    />
                  </div>
                  {/* Slug/API Name hidden from admin as requested */}
                  <input type="hidden" value={fieldForm.fieldName} />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Field Type *</label>
                  <select
                    value={fieldForm.fieldType}
                    onChange={(e) => setFieldForm({ ...fieldForm, fieldType: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {FIELD_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium">Required Field</span>
                  </label>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Placeholder</label>
                  <input
                    type="text"
                    value={fieldForm.placeholder}
                    onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                    placeholder="e.g., Enter your years of teaching experience"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Hint text shown in empty input field</p>
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Help Text</label>
                  <input
                    type="text"
                    value={fieldForm.helpText}
                    onChange={(e) => setFieldForm({ ...fieldForm, helpText: e.target.value })}
                    placeholder="e.g., Total years of professional teaching experience"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Additional explanation shown below the field</p>
                </div>

                {['dropdown', 'multiselect', 'radio'].includes(fieldForm.fieldType) && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Options (one per line)</label>
                    <textarea
                      value={fieldForm.options}
                      onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                      rows={4}
                      placeholder="Primary School&#10;Secondary School&#10;High School&#10;(Enter each option on a new line)"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">List each choice on a separate line</p>
                  </div>
                )}

                {fieldForm.fieldType === 'number' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Min Value</label>
                      <input
                        type="number"
                        value={fieldForm.validation.min}
                        onChange={(e) => setFieldForm({
                          ...fieldForm,
                          validation: { ...fieldForm.validation, min: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Value</label>
                      <input
                        type="number"
                        value={fieldForm.validation.max}
                        onChange={(e) => setFieldForm({
                          ...fieldForm,
                          validation: { ...fieldForm.validation, max: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </>
                )}

                {['text', 'textarea'].includes(fieldForm.fieldType) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Min Length</label>
                      <input
                        type="number"
                        value={fieldForm.validation.minLength}
                        onChange={(e) => setFieldForm({
                          ...fieldForm,
                          validation: { ...fieldForm.validation, minLength: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Max Length</label>
                      <input
                        type="number"
                        value={fieldForm.validation.maxLength}
                        onChange={(e) => setFieldForm({
                          ...fieldForm,
                          validation: { ...fieldForm.validation, maxLength: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save size={18} />
                  Save Field
                </button>
                <button
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Fields List */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Current Fields ({fields.length})</h3>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : fields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {isTemporary ? 'No fields added yet. Add fields before saving the designation.' : 'No fields configured'}
              </div>
            ) : (
              <div className="space-y-2">
                {fields.map((field) => (
                  <div
                    key={field._id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <GripVertical className="text-gray-400" size={20} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{field.fieldLabel}</span>
                          {field.required && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                          )}
                          {field.isSystemField && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">System</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {field.fieldName} • {field.fieldType}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(field)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(field)}
                        disabled={field.isSystemField}
                        className={`p-2 rounded ${
                          field.isSystemField
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          {isTemporary && (
            <p className="text-sm text-gray-600">
              ℹ️ Configure fields, then click "Save Designation" to save everything
            </p>
          )}
          <div className="flex gap-2 ml-auto">
            {isTemporary ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFinish}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Check size={18} />
                  Save Designation & Fields
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldManager;
